import React, { useState, useEffect } from "react";
import {
  Col,
  Container,
  Row,
  FormGroup,
  Button,
  Card,
  Label,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { post, put, get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { toast, Bounce } from "react-toastify";
import imgupload from "../../../assets/images/imageupload.svg";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { values } from "lodash";
import { stateToHTML } from "draft-js-export-html";
import { useSelector } from "react-redux";
import { ImageDrop } from "quill-image-drop-module";
import ReactQuill, { Quill } from "react-quill";
import QuillResizeImage from "quill-resize-image";
import "react-quill/dist/quill.snow.css";
Quill.register("modules/resize", QuillResizeImage);
Quill.register("modules/imageDrop", ImageDrop);

const AddBlog = () => {
  const [equipmentFiles, setEquipmentFiles] = useState({ files: [] });
  const [isUpdate, setIsUpdate] = useState(false);
  {
    isUpdate
      ? (document.title = "Edit Blog | Elevated - Admin Dashboard")
      : (document.title = "Add Blog | Elevated - Admin Dashboard");
  }
  const [blogId, setBlogId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { appRole } = useSelector((state) => {
    return state.auth.currentUser;
  });
  const [initialValues, setInitialValues] = useState({
    title: "",
    content: "",
    file: "",
  });
  const [image, setImage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const blogFromParams = searchParams.get("blogId");
    setBlogId(blogFromParams);

    if (blogFromParams) {
      setIsUpdate(true);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchBlogDetails = async (id) => {
      try {
        const response = await get(`${endpoints.blog}/${id}`);
        if (response?.data) {
          const blogData = response.data.data;
          setInitialValues({
            title: blogData.title || "",
            content: JSON.stringify(blogData?.content) || "",
            file: blogData.File || "",
          });
          setEditorState(blogData?.content?.htmlContent);
          if (blogData.File) {
            const imageUrl = `${process.env.REACT_APP_IMG_URL}${blogData.File}`;
            setImage(imageUrl);
          }
        } else {
          toast.error("No data found for the blog ID", {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          });
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
        toast.error("Failed to fetch blog details. Please try again.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
    };

    if (isUpdate && blogId) {
      fetchBlogDetails(blogId);
    }
  }, [isUpdate, blogId]);
  const validationSchema = Yup.object().shape({
    file: Yup.mixed().test(
      "file-required",
      "At least one image is required",
      (value) => equipmentFiles.files.length > 0 || initialValues.file
    ),
    title: Yup.string().required("Blog title is required"),
    content: Yup.string().required("Please provide content."),
  });

  const handleFileChange = (e) => {
    const files = e.target.files;

    if (files.length > 1) {
      toast.error("Only one image can be uploaded", {
        position: "bottom-center",
      });
      e.target.value = "";
      return;
    }

    const file = files[0];

    if (
      file &&
      (file.type.startsWith("image/") || file.type.startsWith("video/"))
    ) {
      setEquipmentFiles({ files: [file] });
    } else {
      toast.error("Please upload a valid image file", {
        position: "bottom-center",
      });
      setEquipmentFiles({ files: [] });
      e.target.value = "";
    }
  };

  const handleFileRemove = () => {
    setEquipmentFiles({ files: [] });
    setImage("");
    document.getElementById("equipmentFileInput").value = "";
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("content", values.content);
    formData.append(
      "status",
      appRole == "admin" || appRole == "staff" ? "publish" : "draft"
    );

    if (equipmentFiles.files.length > 0) {
      formData.append("file", equipmentFiles.files[0]);
    } else if (initialValues.file && !equipmentFiles.files.length) {
      formData.append("file", initialValues.file);
    }

    try {
      setIsSubmitting(true);
      if (isUpdate) {
        await put(`${endpoints.blog}/${blogId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog Updated Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      } else {
        await post(endpoints.blog, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog Added Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
      navigate("/list-blog");
    } catch (error) {
      console.error(`Error ${isUpdate ? "updating" : "adding"} blog:`, error);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const [editorState, setEditorState] = useState();

  const handleEditorChange = (value, setFieldValue) => {
    setEditorState(value);

    // Convert to raw content and HTML (optional since ReactQuill works directly with HTML)
    const content = value; // ReactQuill content is already HTML
    const rawContent = JSON.stringify({ htmlContent: content });

    setFieldValue("content", rawContent); // Set the field value as required
  };

  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const quill = this.quillRef.getEditor();
          const range = quill.getSelection();
          quill.insertEmbed(range.index, "image", reader.result);
        };
        reader.readAsDataURL(file); // Convert image to base64
      }
    };
  };
  return (
    <Container fluid>
      <Row>
        <Col>
          <Card className="rounded-4">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, resetForm, setFieldValue, setErrors }) => (
                <Form className="p-4">
                  <h3 className="primary-black ff-sora font-size-20 fw-bold">
                    {isUpdate ? "Edit blog" : "Add New Blog"}
                  </h3>

                  <FormGroup>
                    <Label
                      for="file"
                      className="secondary-black ff-sora font-size-16 fw-normal"
                    >
                      Media <span className="text-danger">*</span>
                    </Label>
                    <div
                      className="cursor-pointer overflow-hidden position-relative ff-sora font-size-16 fw-normal secondary-black rounded-5 bg-light d-flex flex-column justify-content-center align-items-center"
                      style={{
                        border: "1px dashed #ccc",
                        width: "160px",
                        height: "160px",
                      }}
                      onClick={() =>
                        document.getElementById("equipmentFileInput").click()
                      }
                    >
                      {equipmentFiles.files.length > 0 ? (
                        equipmentFiles.files.slice(-1).map((file, index) => (
                          <div
                            className="position-relative w-100 h-100"
                            key={index}
                          >
                            {file.type.startsWith("image/") ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt="Uploaded"
                                className="w-100 h-100 object-fit-cover"
                              />
                            ) : (
                              <video
                                src={URL.createObjectURL(file)}
                                alt="Uploaded"
                                height="80px"
                                width="80px"
                                controls
                              />
                            )}

                            <button
                              className="rounded-5"
                              style={{
                                fontSize: "12px",
                                color: "black",
                                cursor: "pointer",
                                zIndex: 1,
                                position: "absolute",
                                top: 10,
                                right: 15,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileRemove();
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))
                      ) : image ? (
                        <img
                          src={image}
                          alt="Uploaded"
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <>
                          <img
                            src={imgupload}
                            alt="Upload"
                            height="40px"
                            width="40px"
                          />
                          <div className="secondary-black text-center mt-2 ff-sora font-size-16 fw-normal">
                            Upload Media
                          </div>
                        </>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        id="equipmentFileInput"
                        className="d-none secondary-black-text ff-sora font-size-16 fw-normal"
                        style={{
                          width: "160px",
                          height: "160px",
                          border: "none",
                        }}
                        onChange={(e) => {
                          handleFileChange(e);
                          setErrors({ file: "" });
                        }}
                      />
                    </div>

                    {errors.file && touched.file && (
                      <div className="text-danger font-size-12 ff-sora fw-normal my-1">
                        {errors.file}
                      </div>
                    )}
                  </FormGroup>
                  <h5 className="secondary-black ff-sora font-size-16 fw-normal">
                    General Info <span className="text-danger">*</span>
                  </h5>
                  <Row>
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      className="me-lg-1 mb-1 mb-md-0"
                    >
                      <FormGroup>
                        <Field
                          name="title"
                          type="text"
                          placeholder="Title"
                          style={{
                            width: "100%",
                            maxWidth: "412px",
                            height: "56px",
                            border: "none",
                          }}
                          className={`rounded-5 bg-light ff-sora font-size-16 fw-normal px-4 ${
                            errors.title && touched.title ? " is-invalid" : ""
                          }`}
                        />
                        {errors.title && touched.title && (
                          <div className="text-danger font-size-12 ff-sora fw-normal my-1">
                            {errors.title}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      className="me-lg-1 mb-1 mb-md-0"
                    >
                      <FormGroup>
                        {/* <Editor
                          editorState={editorState}
                          toolbarClassName="toolbarClassName"
                          wrapperClassName="wrapperClassName"
                          editorClassName="editorClassName"
                          onEditorStateChange={(state) =>
                            handleEditorChange(state, setFieldValue)
                          }
                        /> */}
                        <ReactQuill
                          value={editorState}
                          onChange={(state) => {
                            handleEditorChange(state, setFieldValue);
                          }}
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ header: "1" }, { header: "2" }, { font: [] }],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["bold", "italic", "underline", "strike"],
                              [{ color: [] }, { background: [] }],
                              [{ align: [] }],
                              ["link"],
                              ["clean"],
                              ["image"],
                            ],
                            imageDrop: true,
                            resize: {
                              locale: {},
                            },
                            clipboard: true, // Enable the custom clipboard module
                          }}
                          formats={{ formats: ["image"] }}
                          className="custom-editor"
                        />
                        {errors?.content && touched?.content && (
                          <div className="text-danger font-size-12 ff-sora fw-normal my-1">
                            {errors?.content}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="submit"
                      className="me-2 rounded-pill"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        isUpdate ? (
                          <span>Updating...</span>
                        ) : (
                          <span>Adding...</span>
                        )
                      ) : isUpdate ? (
                        "Update Blog"
                      ) : (
                        "Add Blog"
                      )}
                    </Button>
                    <Button
                      color="white"
                      className="rounded-pill1"
                      onClick={() => {
                        resetForm();
                        setEquipmentFiles({ files: [] });
                        setImage("");
                        if (isUpdate) {
                          navigate("/list-blog");
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              height: "60px",
              position: "sticky",
              bottom: "0",
              zIndex: "10",
            }}
          ></div>
        </Col>
      </Row>
    </Container>
  );
};

export default AddBlog;
